import type { Options } from "@segment/analytics-next"
import * as Sentry from "@sentry/browser"
import type { ScopeContext } from "@sentry/types"
import { jwtDecode } from "jwt-decode"
import { disconnectPusher, resetPusher, subscribeToPusherChannel } from "../pusher"
import { initializeAlgoliaWindow } from "../util/algolia"
import { shouldOpenNewWindowFromMarketplace } from "../util/checkMarketplace"
import { refreshFeatureFlags } from "../util/launchDarkly"
import { getParameterByName, parseJsonOrTellSentry } from "../util/util"
import type { Marketplace } from "./marketplace"
import { AuthenticateSSO } from "./marketplace"
import { finishInactiveSpan, startInactiveSpan } from "./ports/instrumentation"
import type {
  ElmApp,
  ExpandedOnboardingPlans,
  InactiveFeatureFlags,
  MostRecentUser,
  PrivateSavedViews,
  TableViewNodeType,
} from "./windowElm"
import type { Config } from "@ironzion/frontend-js-config"

const appName = "web_app"
const jwtAuthKey = "authentication"
const destination = "initialPath"
export const cookieJwtAuth = "authorizationJwt"

const queryParamsFromUrl = (pageUrl: string): { [key: string]: string } => {
  const url = new URL(pageUrl)
  const pageSearch = new URLSearchParams(url.search)

  const out: { [key: string]: string } = {}
  for (const [key, value] of pageSearch.entries()) {
    out[key] = value
  }
  return out
}

const clearSessionState = (
  config: Config,
  triggersAnalytics: boolean,
  mostRecentUser: MostRecentUser,
  redirectUri?: string | null,
): void => {
  // Make sure to capture all private saved views so they survive the localstorage clear
  const allSavedViews = getAllSavedViews()

  // This is synchronous
  localStorage.clear()

  // Re-add the saved views
  persistSavedViews(allSavedViews)
  if (mostRecentUser) {
    updateMostRecentUser(mostRecentUser)
  }

  // reset segment analytics when we're logging out.
  // This will stop tracking the user until they're identified again.
  // This is important for support sessions to ensure the support session isn't accidently identified as a real session.
  // The app is generally protected, but pages outside of the app can sometimes identify a support session.
  if (!triggersAnalytics) {
    void window.analytics.reset()
  }

  // Disconnect the pusher client unauthenticated Pusher client

  disconnectPusher().catch(console.error)

  // Expire the auth cookie
  document.cookie = `${cookieJwtAuth}= ;domain=.${config.baseDomain}; expires = Thu, 01 Jan 1970 00:00:00 GMT`

  // hard reload or redirect
  if (redirectUri) {
    window.location.href = redirectUri
  } else {
    location.reload()
  }
}

const updateMostRecentUser = (mostRecentUser: MostRecentUser): void => {
  if (mostRecentUser === null) {
    localStorage.removeItem("most_recent_user")
  } else {
    localStorage.setItem("most_recent_user", JSON.stringify(mostRecentUser))
  }
}

const updateExpandedOnboardingPlanIds = (plans: ExpandedOnboardingPlans | null): void => {
  if (plans === null) {
    localStorage.removeItem("expanded_onboarding_plan_ids")
  } else {
    localStorage.setItem("expanded_onboarding_plan_ids", JSON.stringify(plans))
  }
}

const setDismissedOnboardingCTA = (): void => {
  localStorage.setItem("dismissed_onboarding_cta", "true")
}

function dismissScreenSizeWarning(): void {
  window.localStorage.setItem("dismissed-screen-size-warning", "true")
}

function onPillarMouseOver(data: { id: string }): void {
  const video = document.getElementById(data.id) as HTMLVideoElement
  if (video && video.paused) {
    void video.play()
  }
}

function onPillarMouseOut(data: { id: string }): void {
  const video = document.getElementById(data.id) as HTMLVideoElement
  if (video) {
    void video.pause()
  }
}

// Private saved views

const privateSavedViewKey = "private_saved_views"

const privateSavedViewAllKeys = privateSavedViewKey + "_all_keys"

const requestPrivateSavedViews = (app: ElmApp, nodeType: TableViewNodeType): void => {
  const privateViewJson = localStorage.getItem(privateSavedViewKey + nodeType)

  if (app.ports.privateSavedViews && privateViewJson !== null) {
    app.ports.privateSavedViews.send(parseJsonOrTellSentry("Saved views in local storage", privateViewJson))
  }
}

const updatePrivateSavedViews = (savedViews: PrivateSavedViews): void => {
  const key = privateSavedViewKey + savedViews.nodeType
  // Every time we update the saved views, we also update the list of all keys
  const allKeys = getAllSavedViewKeys()
  if (!allKeys.includes(key)) {
    allKeys.push(key)
  }

  localStorage.setItem(privateSavedViewAllKeys, JSON.stringify(allKeys))

  // persist the saved views
  localStorage.setItem(key, JSON.stringify(savedViews))
}

const getAllSavedViewKeys = (): string[] => {
  const allKeys = localStorage.getItem(privateSavedViewAllKeys)
  if (allKeys === null) {
    return []
  } else {
    const parsed = parseJsonOrTellSentry<string[]>("Getting all private saved views from local storage", allKeys)
    if (parsed === null) {
      return []
    }
    return parsed
  }
}

/**
 * Retrieve saved views from storage, to be passed to the Elm runtime.
 *
 * Using `unknown` as the return value here so we can treat it as opaque; it's
 * typed and decoded in Elm!
 */
const getAllSavedViews = (): { [key: string]: unknown } => {
  const savedViews: { [key: string]: unknown } = {}
  const allKeys = getAllSavedViewKeys()
  for (const key of allKeys) {
    const privateViewJson = localStorage.getItem(key)

    if (!privateViewJson) {
      continue
    }

    const parsedOrNull = parseJsonOrTellSentry("Saved views in local storage", privateViewJson)

    if (parsedOrNull !== null) {
      savedViews[key] = parsedOrNull
    }
  }
  return savedViews
}

const persistSavedViews = (savedViews: { [key: string]: unknown }): void => {
  const keys = []
  for (const key in savedViews) {
    keys.push(key)
    localStorage.setItem(key, JSON.stringify(savedViews[key]))
  }
  localStorage.setItem(privateSavedViewAllKeys, JSON.stringify(keys))
}

const updateInactiveFeatureFlags = (inactiveFeatureFlags: InactiveFeatureFlags): void => {
  localStorage.setItem("inactive_feature_flags", JSON.stringify(inactiveFeatureFlags))
}

export const readTriggersAnalyticsFlagFromJwt = (jwt: string | null, defaultIfNotReadable: boolean): boolean => {
  if (jwt) {
    let jwtObject
    try {
      jwtObject = jwtDecode<{ triggersAnalytics: null | boolean }>(jwt)
      if (jwtObject.triggersAnalytics !== null) {
        return jwtObject.triggersAnalytics
      }
    } catch (err) {
      return defaultIfNotReadable
    }
  }

  if (window.location.pathname.startsWith("/create-account")) {
    return true
  }

  return defaultIfNotReadable
}

export type PortOpts = {
  basePath: string
  triggersAnalytics: boolean
  marketplace: Promise<Marketplace | null>
}

export const bindPorts = (config: Config, app: ElmApp, opts: PortOpts) => {
  const marketplaceChannel = opts.marketplace

  // Redirect the entire browser window elsewhere
  app.ports.redirect &&
    app.ports.redirect.subscribe(async function (url) {
      if (shouldOpenNewWindowFromMarketplace()) {
        return await AuthenticateSSO.openInPopup(url, { marketplaceChannel: await marketplaceChannel, type: "google" })
      }

      window.location.href = url
    })

  app.ports.resetWindowScroll &&
    app.ports.resetWindowScroll.subscribe(function () {
      window.scrollTo(0, 0)
    })

  app.ports.toWorld &&
    app.ports.toWorld.subscribe(function (fromElm) {
      switch (fromElm.tag) {
        case "FocusAndSelect":
          setTimeout(() => {
            // in some cases the element hasn't been rendered yet
            const elem = document.getElementById(fromElm.data.id)
            if (elem) {
              elem.focus()
              ;(elem as HTMLInputElement).select()
            }
          }, 100)
          break

        case "UpdateMostRecentUser":
          updateMostRecentUser(fromElm.data)
          break

        case "UpdateInactiveFeatureFlags":
          updateInactiveFeatureFlags(fromElm.data)
          break

        case "ClearSessionStateAndHardReload":
          clearSessionState(config, opts.triggersAnalytics, fromElm.data)
          break

        case "RequestExpandedOnboardingPlanIds":
          requestExpandedOnboardingPlanIds()
          break

        case "UpdateExpandedOnboardingPlanIds":
          updateExpandedOnboardingPlanIds(fromElm.data)
          break

        case "SetDismissedOnboardingCTA":
          setDismissedOnboardingCTA()
          break

        case "PrivateSavedViewsRequested":
          requestPrivateSavedViews(app, fromElm.data)
          break

        case "PrivateSavedViewsUpdated":
          updatePrivateSavedViews(fromElm.data)
          break

        case "UpdateSidebarMode":
          updateSidebarMode(fromElm.data)
          break

        case "DismissScreenSizeWarning":
          dismissScreenSizeWarning()
          break

        case "StartInactiveSpan":
          startInactiveSpan(fromElm.data)
          break

        case "FinishInactiveSpan":
          finishInactiveSpan(fromElm.data)
          break

        case "OnPillarMouseOver":
          onPillarMouseOver(fromElm.data)
          break

        case "OnPillarMouseOut":
          onPillarMouseOut(fromElm.data)
          break

        case "SendMarketplaceMessage": {
          void marketplaceChannel.then((channel) => channel?.sendEvent(fromElm.data))
          break
        }

        default: {
          const exhaustive: never = fromElm
          console.warn(`Unhandled port tag ${JSON.stringify(exhaustive)}`)
          break
        }
      }
    })

  app.ports.logout &&
    app.ports.logout.subscribe(function () {
      const redirectUri = getParameterByName("redirectUri")

      clearSessionState(config, opts.triggersAnalytics, null, redirectUri)
      try {
        // Note that `if (Sentry)` will not work here because
        // the code will still crash if Sentry is not defined.
        //
        // if (Sentry) {}                 --> ReferenceError: Sentry is not defined
        // typeof Sentry !== 'undefined'  --> false
        if (typeof Sentry !== "undefined") {
          Sentry.setUser(null)
          Sentry.setTags({})
        }
      } catch (err) {
        console.warn("Unable to clear Sentry information", err)
      }

      useIntercom("logout", (Intercom) => {
        Intercom("shutdown")
      })

      window.location.href = redirectUri ?? config.logoutUrl
    })

  app.ports.retryLogin &&
    app.ports.retryLogin.subscribe(function () {
      clearSessionState(config, opts.triggersAnalytics, null)
      app.ports.redirectToLogin && app.ports.redirectToLogin.send(null)
    })

  app.ports.saveJwt &&
    app.ports.saveJwt.subscribe(async function (jwt) {
      try {
        opts.triggersAnalytics = readTriggersAnalyticsFlagFromJwt(jwt, opts.triggersAnalytics)

        localStorage.setItem(jwtAuthKey, jwt)

        await resetPusher(config, jwt)
      } catch (e) {
        console.log(e)
      }
    })

  app.ports.setupAlgolia &&
    app.ports.setupAlgolia.subscribe(function (searchApiToken) {
      initializeAlgoliaWindow(config, searchApiToken)
    })

  app.ports.saveInitialPath &&
    app.ports.saveInitialPath.subscribe(function (url) {
      if (url === null) {
        localStorage.removeItem(destination)
      } else {
        try {
          localStorage.setItem(destination, url)
        } catch (e) {
          console.log(e)
        }
      }
    })

  app.ports.tagUserForFeatureFlags &&
    app.ports.tagUserForFeatureFlags.subscribe(async function (tags) {
      const logError = (error: Error) => {
        if (Sentry) {
          if (tags?.user) {
            if (tags.user.key && tags.user.email) {
              Sentry.setUser({
                id: tags.user.key,
                email: tags.user.email,
              })
            }

            const sentryTags: { [key: string]: string } = {}

            if (tags.user.key) {
              sentryTags.userId = tags.user.key
            }

            if (tags.user.email) {
              sentryTags.userEmail = tags.user.email
            }

            if (tags.user.organizationId) {
              sentryTags.organizationId = tags.user.organizationId
            }

            Sentry.setTags(sentryTags)
          }

          Sentry.captureException(error)
        } else {
          console.error(error)
        }
      }

      // Lauch Darkly feature flags
      try {
        await refreshFeatureFlags(
          config,
          tags,
          (newFlags) => {
            const value = newFlags ? newFlags : []
            app.ports.launchDarklyFeatureFlags.send(value)
          },
          logError,
        )
      } catch (error) {
        logError(error)
      }
    })

  // Subscribe to our two main push event channels, for the individual and the organization
  app.ports.setupPushEventSubscriptions &&
    app.ports.setupPushEventSubscriptions.subscribe(async function ([organizationId, userId]) {
      // The authentication endpoint expects these channel names to be in this format
      const orgChannel = await subscribeToPusherChannel(config, `private-org;${organizationId}`)

      if (orgChannel) {
        orgChannel.bind("OrganizationPushUpdate", function (data: unknown) {
          if (app.ports.pushEventOrganization) {
            app.ports.pushEventOrganization.send(data)
          }
        })
      }

      // The authentication endpoint expects these channel names to be in this format
      const individualChannel = await subscribeToPusherChannel(config, `private-individual;${organizationId};${userId}`)

      if (individualChannel) {
        individualChannel.bind("IndividualPushUpdate", function (data: unknown) {
          if (app.ports.pushEventIndividual) {
            app.ports.pushEventIndividual.send(data)
          }
        })
      }
    })

  // Workflow Test Mode - local storage BEGIN

  const workflowTestModeKeyPrefix = "workflowTestModeEdits_"
  const workflowTestModeKey = (uuid: string) => `${workflowTestModeKeyPrefix}${uuid}`

  const cleanupWorkflowTestModeLocalStorageData = () => {
    const twoHoursAgo = new Date(new Date().getTime() - 2 * 60 * 60 * 1000)

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(workflowTestModeKeyPrefix)) {
        const jsonString = localStorage.getItem(key)
        try {
          if (jsonString == null) {
            localStorage.removeItem(key)
          } else {
            const { edits, timestamp } = JSON.parse(jsonString)
            if (timestamp < twoHoursAgo) {
              localStorage.removeItem(key)
            }
          }
        } catch (error) {}
      }
    }
  }

  app.ports.workflowTestModeSaveEdits &&
    app.ports.workflowTestModeSaveEdits.subscribe(function ({ uuid, edits }) {
      cleanupWorkflowTestModeLocalStorageData()
      const now = +new Date()
      const jsonString = JSON.stringify({ edits, timestamp: now })
      localStorage.setItem(workflowTestModeKey(uuid), jsonString)
    })

  // The `Page.RequestLauncher.Page.init` function will run twice, with two
  // different sets of feature flags. If we react to the
  // `workflowTestModeLoadEdits` request the first time, the edits will go to
  // waste since the flag WorkflowTestModeUnsavedEdits will not be on.
  // We need to ignore the first request and act on the second one.
  let workflowTestModeFirstRequestSeenAndSkipped = false

  app.ports.workflowTestModeLoadEdits &&
    app.ports.workflowTestModeLoadEdits.subscribe(function ({ uuid }) {
      if (workflowTestModeFirstRequestSeenAndSkipped) {
        cleanupWorkflowTestModeLocalStorageData()
        const key = workflowTestModeKey(uuid)
        const jsonString = localStorage.getItem(key)
        try {
          if (jsonString == null) {
            app.ports.workflowTestModeEditsLoadedAndCleared.send(null)
          } else {
            const { edits, timestamp } = JSON.parse(jsonString)
            localStorage.removeItem(key)
            app.ports.workflowTestModeEditsLoadedAndCleared.send(edits)
          }
        } catch (error) {
          app.ports.workflowTestModeEditsLoadedAndCleared.send(null)
        }
      } else {
        workflowTestModeFirstRequestSeenAndSkipped = true
      }
    })

  // Workflow Test Mode - local storage END

  // Chili Piper BEGIN

  app.ports.chiliPiperSubmit_ &&
    app.ports.chiliPiperSubmit_.subscribe(function (args) {
      if (window.ChiliPiper) {
        window.ChiliPiper.submit(...args)
      }
    })

  // Chili Piper END

  // Segment BEGIN

  app.ports.segmentIdentify &&
    app.ports.segmentIdentify.subscribe(async function (params) {
      const [userId, traits] = params

      try {
        if (typeof Sentry !== "undefined") {
          // Update Sentry with information about the user
          Sentry.setUser({
            id: userId,
            email: traits.email,
          })

          // Set the relevant tags for Sentry
          Sentry.setTags({
            userId,
            userEmail: traits.email,
            organizationId: traits.company.id,
            organizationName: traits.company.name,
            hasPremium: traits.hasPremium,
            hasPremiumIntelligence: traits.hasPremiumIntelligence,
            hasPremiumNegotiation: traits.hasPremiumNegotiation,
            hasPremiumProcurement: traits.hasPremiumProcurement,
          })
        }
      } catch (err) {
        console.warn("Unable to set user information in Sentry", err)
      }

      // if we are muting analytics, return now
      if (!opts.triggersAnalytics) {
        return
      }

      // Identify them in Segment
      await window.analytics.identify(userId, traits, { context: { app: { name: appName } } })

      // Immediately follow up by grouping them.
      await window.analytics.group(traits.company.id, {
        name: traits.company.name,
        domain: traits.company.domain,
        hasPremium: traits.hasPremium,
        hasPremiumIntelligence: traits.hasPremiumIntelligence,
        hasPremiumNegotiation: traits.hasPremiumNegotiation,
        hasPremiumProcurement: traits.hasPremiumProcurement,
      })

      try {
        /* global window */
        // Collect NPS feedback
        // DEBUGGING: replace "survey" with "test", see https://docs.vitally.io/using-vitallys-nps-surveys/installing-and-configuring-nps-surveys for details
        if (window.Vitally && typeof window.Vitally.nps === "function") {
          window.Vitally.nps("survey", {
            autoLoadSegment: true,
            productName: "Vendr",
          })
        }
      } catch (error) {
        if (Sentry) {
          Sentry.captureException(error)
        } else {
          console.error(error)
        }
      }
    })

  // Update specific traits for a user
  app.ports.segmentIdentifyUpdate &&
    app.ports.segmentIdentifyUpdate.subscribe(async function (params) {
      const [userId, traits] = params

      // if we are muting analytics, return now
      if (!opts.triggersAnalytics) {
        return
      }

      // Update traits in Segment
      await window.analytics.identify(userId, traits, { context: { app: { name: appName } } })
    })

  app.ports.segmentTrack &&
    app.ports.segmentTrack.subscribe(async function (args) {
      if (!opts.triggersAnalytics) {
        return
      }

      const properties = args.properties
      properties.page = {
        path: window.location.pathname,
        referrer: window.document.referrer,
        search: queryParamsFromUrl(window.location.href),
        title: window.document.title,
        url: window.location.href,
        language: window.navigator.language,
      }

      const context = args.context
      context.app = {
        name: appName,
        ...context.app,
      }

      // *ANALYTICS LOGGER* If you are testing analytics,
      // Uncomment the following line to log events being sent to segment
      // console.log("Sending to Segment", {event: args.event, properties, context: args.context.context})

      // https://segment.com/docs/connections/destinations/catalog/userlist/#track
      await window.analytics.track(args.event, properties, context)
    })

  app.ports.segmentGroup &&
    app.ports.segmentGroup.subscribe(async function (args) {
      if (!opts.triggersAnalytics) {
        return
      }
      const groupId = args[0]
      const properties = args[1]
      await window.analytics.group(groupId, properties)
    })

  app.ports.segmentAlias &&
    app.ports.segmentAlias.subscribe(async function (args) {
      if (!opts.triggersAnalytics) {
        return
      }

      const anonymousId = readCookieValue("ajs_anonymous_id")
      if (!anonymousId) {
        return
      }

      await window.analytics.alias(args.userId, anonymousId)
    })

  app.ports.segmentPage &&
    app.ports.segmentPage.subscribe(async function (props) {
      if (!opts.triggersAnalytics) {
        return
      }
      const groupId = props.groupId
      const options: Options = {
        page_name: props.pageName,
        page_path: window.location.pathname,
        page_url: window.location.href,
        page_language: window.navigator.language,
        page_search: queryParamsFromUrl(window.location.href),
        is_impersonation: props.isImpersonation,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        search: window.location.hash,
      }
      const utmParameters = Object.fromEntries(props.utmParameters)
      const campaign = {
        // TODO: all these have to be cast to strings despite maybe being null
        // because of how we're lining up types. It would be possible to
        // eliminate the mismatch here with some effort. Is it worth it,
        // though?
        name: utmParameters.utm_campaign as string,
        source: utmParameters.utm_source as string,
        medium: utmParameters.utm_medium as string,
        term: utmParameters.utm_term as string,
        content: utmParameters.utm_content as string,
      }

      const isReferrerMarketplace = /www\.google\.com\/url/gm
      if (isReferrerMarketplace.test(document.referrer)) {
        // If the marketplace is the referrer (which goes
        // through google.com/url, discard by using well known
        // referrer exclusion, example.com so as to maintain FT tracking.
        // For notes on how this works between GA & Segment, see here:
        // https://github.com/segment-integrations/analytics.js-integration-google-analytics/pull/25
        options.referrer = "http://example.com"
      } else {
        if (!document.referrer || document.referrer === "") {
          // Sometimes the referrer is blank.
          // But the app knows what page we were just on.
          options.referrer = props.previousPage
        } else {
          options.referrer = document.referrer
        }
      }
      // *ANALYTICS LOGGER* If you are testing analytics,
      // Uncomment the following line to log events being sent to segment
      // console.log("Sending to Segment", { context: props, options })

      await window.analytics.page(props.pageName, groupId, options, {
        context: { app: { name: appName }, groupId: groupId, campaign: campaign },
      })
    })

  // Segment END

  function useIntercom(action: string, handle: (intercom: Exclude<typeof window.Intercom, undefined>) => void): void {
    try {
      if (window.Intercom) {
        handle(window.Intercom)
      } else {
        console.warn(`Unable to ${action} Intercom: Not loaded`)
      }
    } catch (err) {
      console.warn(`Unable to ${handle} Intercom: ${err}`)
    }
  }

  app.ports.setupIntercom &&
    app.ports.setupIntercom.subscribe(function (params) {
      useIntercom("boot", async (Intercom) => {
        const isMarketplace = await shouldOpenNewWindowFromMarketplace()
        if (params.enabled && params.userHash && !isMarketplace) {
          Intercom("boot", {
            api_base: "https://api-iam.intercom.io",
            app_id: config.intercomAppId,
            name: params.name,
            email: params.email,
            user_id: params.userId,
            user_hash: params.userHash,
            role: params.role,
            company: {
              id: params.company.id,
              name: params.company.name,
              website: params.company.domain,
              plan: params.company.tier,
            },
            hide_default_launcher: true,
          })
        } else {
          // We need to shutdown because otherwise Intercom will show anyway using the last user
          Intercom("shutdown")
        }
      })
    })

  app.ports.showIntercomSpace_ &&
    app.ports.showIntercomSpace_.subscribe(function (space) {
      // This call opens the intercom messenger and starts a new conversation in our home "space"
      // https://developers.intercom.com/installing-intercom/docs/intercom-javascript#intercomshowspace-spacename
      useIntercom("show space: " + space, (Intercom) => {
        Intercom("showSpace", space)
      })
    })

  app.ports.showIntercomNewMessage &&
    app.ports.showIntercomNewMessage.subscribe(function ({ message }) {
      // This call opens the intercom messenger and starts a new conversation
      // https://developers.intercom.com/installing-intercom/web/methods/#intercomshownewmessage
      useIntercom("show new message", (Intercom) => {
        Intercom("showNewMessage", message || undefined)
      })
    })

  function initializeMergeDev(
    linkToken: string,
    onReady: () => void,
    onSuccess: (publicToken: string) => void,
    onExit?: () => void,
    filePickerConfig?: {
      allowMultiSelect: boolean
      types: string[]
      onSubmit: (outputObjects: { id?: string; name?: string }[]) => void
    },
  ) {
    window.MergeLink?.initialize({
      linkToken: linkToken,
      onReady: onReady,
      onSuccess: onSuccess,
      onExit: onExit,
      filePickerConfig,
    })
  }

  let mergeLinkIsLoading = false

  function ensureMergeDevIsLoaded(callback: () => Promise<void>) {
    const load = () => {
      mergeLinkIsLoading = true
      const scriptTag = window.document.createElement("script")
      scriptTag.src = "https://cdn.merge.dev/initialize.js"
      scriptTag.onload = callback
      window.document.body.append(scriptTag)
    }

    if (window.MergeLink !== undefined) {
      void callback()
    } else if (!mergeLinkIsLoading) {
      load()
    }
  }

  app.ports.openMergeFilePicker &&
    app.ports.openMergeFilePicker.subscribe(async function ({ linkToken, externalId }) {
      ensureMergeDevIsLoaded(async () => {
        const onReady = function () {
          window.MergeLink?.openLink()
        }

        const onSuccess = function (publicToken: string) {
          app.ports.mergeDevPublicTokenReceived &&
            app.ports.mergeDevPublicTokenReceived.send({
              publicToken: publicToken,
              externalId: externalId,
            })
        }

        const onSubmit = function (outputObjects: { id?: string; name?: string }[]): void {
          const outputObject = outputObjects[0]

          if (!outputObject || !outputObject.id || !outputObject.name) {
            console.error("ERROR: Invalid response from filepicker")
            return
          }
          app.ports.mergeDevFolderSelected &&
            app.ports.mergeDevFolderSelected.send({ name: outputObject.name, id: outputObject.id })
        }

        const onExit = function () {
          app.ports.mergeDevFilePickerExited && app.ports.mergeDevFilePickerExited.send(null)
        }

        initializeMergeDev(linkToken, onReady, onSuccess, onExit, {
          allowMultiSelect: false,
          types: ["FOLDER"],
          onSubmit,
        })
      })
    })

  app.ports.openMergeLink &&
    app.ports.openMergeLink.subscribe(function ([linkToken, externalId]) {
      ensureMergeDevIsLoaded(async function () {
        const onReady = function () {
          window.MergeLink?.openLink()
        }

        const onSuccess = function (publicToken: string) {
          app.ports.mergeDevPublicTokenReceived &&
            app.ports.mergeDevPublicTokenReceived.send({ publicToken: publicToken, externalId: externalId })
        }

        initializeMergeDev(linkToken, onReady, onSuccess)
      })
    })

  app.ports.openWindow &&
    app.ports.openWindow.subscribe(function (url) {
      console.log("opening")

      // Workaround: Safari blocks window.open when it's not the direct result of user interaction
      const a = document.createElement("a")
      a.setAttribute("href", url)
      a.setAttribute("target", "_blank")
      a.click()
    })

  /**
   * Send error to Sentry
   */

  app.ports.sendError &&
    app.ports.sendError.subscribe(function ({
      title,
      details,
      tags,
      extra,
      isImpersonation,
      xAmznRequestId,
      xAmznTraceId,
    }) {
      if (typeof Sentry !== "undefined") {
        const tagsObj: { [key: string]: string } = {}
        tags.map((tag) => {
          tagsObj[tag.key] = tag.value
        })

        // Add the impersonation information
        tagsObj.isImpersonation = isImpersonation ? "yes" : "no"

        // Add the X-Amzn-RequestId and X-Amzn-Trace-Id headers
        if (xAmznRequestId !== null) {
          tagsObj.xAmznRequestId = xAmznRequestId
        }
        if (xAmznTraceId !== null) {
          tagsObj.xAmznTraceId = xAmznTraceId
        }

        // Add a note that this originated from Elm.
        tagsObj.sentFromElm = "yes"

        extra.details = details

        const err: Error & { type?: string } = new Error(title)
        err.name = title
        err.type = title

        const context: Partial<ScopeContext> = {
          tags: tagsObj,
          extra,
        }

        if (IS_DEVELOPMENT) {
          console.error(err, context)
        }

        Sentry.captureException(err, context)
      }
    })

  window.addEventListener("storage", function (event) {
    if (event.storageArea === localStorage && event.key === "authentication" && event.newValue) {
      app.ports.newJwt && app.ports.newJwt.send(event.newValue)
    }
  })

  /**
   * Generate a cryptographically secure random string to use as a nonce
   * https://auth0.com/docs/api-auth/tutorials/nonce
   */
  app.ports.authenticateViaSamlSSOUrl &&
    app.ports.authenticateViaSamlSSOUrl.subscribe(async function (opts) {
      const STATE_KEY = "state"
      const stateValue = (() => {
        const url = new URL(opts.authenticationUrl)

        return url.searchParams.get(STATE_KEY) || generateNonce()
      })()

      localStorage.setItem(stateValue, JSON.stringify({ [STATE_KEY]: stateValue, authType: "SAML_SSO" }))

      console.log({ stateValue })

      const authenticationUrlWithState = `${opts.authenticationUrl}&${STATE_KEY}=${stateValue}`
      console.log({ authenticationUrlWithState })

      if (await shouldOpenNewWindowFromMarketplace()) {
        await AuthenticateSSO.openInPopup(authenticationUrlWithState, {
          marketplaceChannel: await marketplaceChannel,
          type: "saml",
        })
      } else {
        window.location.href = authenticationUrlWithState
      }
    })

  app.ports.saveOAuthStateKey &&
    app.ports.saveOAuthStateKey.subscribe(function (input) {
      localStorage.setItem(input.stateKey, JSON.stringify({ state: input.stateKey, authType: input.authProvider }))
      app.ports.oauthStateKeySaved.send({ stateKey: input.stateKey, urlForRedirect: input.urlForRedirect })
    })

  app.ports.saveOAuthStateKeyWithSubdomain &&
    app.ports.saveOAuthStateKeyWithSubdomain.subscribe(function (input) {
      localStorage.setItem(
        input.stateKey,
        JSON.stringify({ state: input.stateKey, authType: input.authProvider, subdomain: input.subdomain }),
      )
      app.ports.oauthStateKeySaved.send({ stateKey: input.stateKey, urlForRedirect: input.urlForRedirect })
    })

  app.ports.saveOAuthStateKeyForDocuSign &&
    app.ports.saveOAuthStateKeyForDocuSign.subscribe(function (input) {
      localStorage.setItem(
        input.stateKey,
        JSON.stringify({
          state: input.stateKey,
          authType: input.authProvider,
        }),
      )
      app.ports.oauthStateKeySaved.send({ stateKey: input.stateKey, urlForRedirect: input.urlForRedirect })
    })

  app.ports.storeWaitingForSamlFlag &&
    app.ports.storeWaitingForSamlFlag.subscribe(function () {
      localStorage.setItem("waiting-for-saml-login-code", "true")
    })

  app.ports.setupBento &&
    app.ports.setupBento.subscribe(({ account, accountUser }) => {
      window.bentoSettings = {
        appId: config.bentoAppId,
        account,
        accountUser: accountUser,
      }

      window.Bento && void window.Bento.reset()
    })

  app.ports.appcuesShowById &&
    app.ports.appcuesShowById.subscribe((id) => {
      if (typeof window.Appcues !== "undefined") {
        window.Appcues.show(id)
      }
    })

  app.ports.modalOpen &&
    app.ports.modalOpen.subscribe((id) => {
      window.requestAnimationFrame(() => {
        for (const modal of document.getElementsByTagName("dialog")) {
          modal.close()
        }

        const modalToOpen = document.getElementById(id)

        if (modalToOpen instanceof HTMLDialogElement) {
          modalToOpen.showModal()
        }
      })
    })

  app.ports.modalCloseAll &&
    app.ports.modalCloseAll.subscribe(() => {
      window.requestAnimationFrame(() => {
        for (const modal of document.getElementsByTagName("dialog")) {
          modal.close()
        }
      })
    })

  app.ports.modalOpenLayered &&
    app.ports.modalOpenLayered.subscribe((id) => {
      window.requestAnimationFrame(() => {
        const modalToOpen = document.getElementById(id)

        if (modalToOpen instanceof HTMLDialogElement) {
          modalToOpen.showModal()
        }
      })
    })

  app.ports.modalCloseLayered &&
    app.ports.modalCloseLayered.subscribe((id) => {
      window.requestAnimationFrame(() => {
        const modalToClose = document.getElementById(id)

        if (modalToClose instanceof HTMLDialogElement) {
          modalToClose.close()
        }
      })
    })

  app.ports.modalOpenInOrder &&
    app.ports.modalOpenInOrder.subscribe((ids) => {
      window.requestAnimationFrame(() => {
        for (const id of ids) {
          const modalToOpen = document.getElementById(id)

          if (modalToOpen instanceof HTMLDialogElement) {
            modalToOpen.showModal()
          }
        }
      })
    })

  /* --- START BLOCK NAVIGATION ---
     If navigation should be blocked, start listening to beforeunload event (fires on page refresh or tab close),
     and prevent default on that event. This shows a native browser warning.
  */
  const beforeUnloadListener = (event: BeforeUnloadEvent): void => {
    event.preventDefault()

    // for legacy browser compatibility. See
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
    event.returnValue = true
  }

  app.ports.sendShouldBlockNavigation.subscribe(function (shouldBlock) {
    if (shouldBlock) {
      window.addEventListener("beforeunload", beforeUnloadListener)
    } else {
      window.removeEventListener("beforeunload", beforeUnloadListener)
    }
  })
  /* --- END BLOCK NAVIGATION --- */

  app.ports.copy &&
    app.ports.copy.subscribe(async function (text) {
      const clipboard = navigator.clipboard
      if (!clipboard) {
        return
      }

      await clipboard.writeText(text)

      app.ports.copySucceeded && app.ports.copySucceeded.send(null)
    })

  const requestExpandedOnboardingPlanIds = () => {
    const expandedOnboardingPlanIds = localStorage.getItem("expanded_onboarding_plan_ids")
    let plans: unknown
    if (expandedOnboardingPlanIds) {
      try {
        plans = JSON.parse(expandedOnboardingPlanIds)
      } catch {
        plans = []
      }
    }
    window.requestAnimationFrame(() => {
      app.ports.expandedOnboardingPlanIds && app.ports.expandedOnboardingPlanIds.send(plans)
    })
  }

  // Cookies
  if (app.ports.requestCookieValue?.subscribe) {
    app.ports.requestCookieValue.subscribe((cookieName) => {
      const cookieValue = readCookieValue(cookieName)

      if (app.ports.receieveCookieValue?.send) {
        app.ports.receieveCookieValue.send({
          name: cookieName,
          value: cookieValue,
        })
      }
    })
  }

  // Client authentication
  if (app.ports.publishTokenExchange?.subscribe) {
    app.ports.publishTokenExchange.subscribe(
      ({ accessToken, accessTokenExpiresAt, chromeExtensionId, refreshToken, refreshTokenExpiresAt }) => {
        if (!chrome || !chrome.runtime) {
          console.warn(`Unable to publish token exchange for non-Chromium browser`)

          return app.ports.subscribeTokenExchange?.send({
            eventId: null,
            message: "Unable to publish token exchange for non-Chromium browser",
            status: "error",
          })
        }

        chrome.runtime.sendMessage(
          chromeExtensionId,
          {
            type: "token_exchanged",
            accessToken,
            accessTokenExpiresAt,
            refreshToken,
            refreshTokenExpiresAt,
          },
          function (response) {
            console.log(`Chrome extension exchange response:`, response)

            if (typeof response === "object" && !Array.isArray(response) && response !== null && "status" in response) {
              return app.ports.subscribeTokenExchange?.send({
                eventId: response.eventId || null,
                message: response.message || null,
                status: response.status,
              })
            }
          },
        )
      },
    )
  }
}

function generateNonce(): string {
  const charset = "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~"
  let result = ""
  let length = 16

  while (length > 0) {
    const bytes = new Uint8Array(16)
    const random = window.crypto.getRandomValues(bytes)

    random.forEach(function (c) {
      if (length === 0) {
        return
      }
      if (c < charset.length) {
        result += charset[c]
        length--
      }
    })
  }

  return result
}

export const sidebarModeKey = "sidebar_mode"

function updateSidebarMode(mode: string | null): void {
  if (mode) {
    window.localStorage.setItem(sidebarModeKey, mode)
  } else {
    window.localStorage.removeItem(sidebarModeKey)
  }
}

function readCookieValue(cookieName: string): string | null {
  const rawStringValue = RegExp(cookieName + "=[^;]+").exec(document.cookie)

  const cookieValue = rawStringValue ? decodeURIComponent(rawStringValue.toString().replace(/^[^=]+./, "")) : null

  return cookieValue
}
